import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getInstances, 
  getInstance as getInstanceFirestore, 
  createInstance as createInstanceFirestore, 
  updateInstance as updateInstanceFirestore, 
  deleteInstance as deleteInstanceFirestore, 
  getTemplate,
  LiveSessionService,
  generateSessionCode
} from '../firebase/unifiedDataService';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../firebase/config';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { useTemplates } from './TemplateContext';
import { normalizeQuestion } from '../utils/llm-generator/questionTransformers';
import { removeActivitiesForSession } from '../utils/studentStatsService';
import { Logger } from '../utils/logger';

const log = Logger.create('InstanceContext');

const InstanceContext = createContext(null);

export function InstanceProvider({ children }) {
  const { user, userProfile } = useUnifiedAuth();
  const { incrementUseCount } = useTemplates();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load instances when user changes (only for teachers/admins)
  useEffect(() => {
    log.debug('useEffect - user:', user?.uid, 'userProfile:', userProfile);
    // Default to 'teacher' role if user is logged in but has no role set
    const role = userProfile?.role || (user && userProfile ? 'teacher' : null);
    log.debug('User role:', role);
    if (user && userProfile && (role === 'teacher' || role === 'admin')) {
      log.debug('Loading instances for role:', role);
      loadInstances();
    } else {
      log.debug('Skipping load - waiting for user/profile or not teacher/admin');
      setInstances([]);
    }
  }, [user, userProfile]);

  // Load all instances for the current user from both Firestore and Realtime DB
  const loadInstances = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Load from BOTH sources and merge (Realtime DB is source of truth for active sessions)
      log.debug('Loading instances for user:', user.uid);
      
      // Get from Firestore (for history/ended sessions)
      let firestoreInstances = [];
      try {
        firestoreInstances = await getInstances(user.uid);
        log.debug('Firestore instances found:', firestoreInstances.length);
      } catch (err) {
        log.warn('Firestore query failed (index may be missing):', err.message);
      }
      
      // Get from Realtime DB (for active sessions)
      let realtimeInstances = [];
      const instancesRef = ref(database, `quiz_instances/${user.uid}`);
      log.debug('Checking Realtime DB path:', `quiz_instances/${user.uid}`);
      const snapshot = await get(instancesRef);
      log.debug('Realtime DB snapshot exists:', snapshot.exists());

      if (snapshot.exists()) {
        const instancesData = snapshot.val();
        log.debug('Realtime DB raw data:', instancesData);
        realtimeInstances = Object.entries(instancesData).map(([id, data]) => ({
          id,
          ...data,
          owner: user.uid
        }));
        log.debug('Realtime DB instances found:', realtimeInstances.length);
      } else {
        log.debug('No data found in Realtime DB');
      }
      
      // Merge: Realtime DB takes priority for active sessions
      const instanceMap = new Map();
      
      // Add Firestore instances first
      firestoreInstances.forEach(inst => {
        instanceMap.set(inst.id, inst);
      });
      
      // Overwrite/add with Realtime DB instances (more up-to-date for active sessions)
      realtimeInstances.forEach(inst => {
        instanceMap.set(inst.id, inst);
      });
      
      const instancesArray = Array.from(instanceMap.values());

      // Sort by startedAt (newest first)
      instancesArray.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));

      setInstances(instancesArray);
      log.success('Total instances loaded:', instancesArray.length);
    } catch (err) {
      log.error('Error loading instances:', err);
      setError('Error loading instances');
    } finally {
      setLoading(false);
    }
  };

  // Get instances by template
  const getInstancesByTemplate = (templateId) => {
    return instances.filter(i => i.templateId === templateId);
  };

  // Get instances by course
  const getInstancesByCourse = (courseId) => {
    if (!courseId) {
      return instances.filter(i => !i.courseId);
    }
    return instances.filter(i => i.courseId === courseId);
  };

  // Get active instances
  const getActiveInstances = () => {
    return instances.filter(i => i.status === 'active');
  };

  // Start a new instance from a template in Firestore
  const startInstance = async (templateId, customName = null) => {
    if (!user) throw new Error('Not authenticated');

    try {
      // Load template from Firestore
      const template = await getTemplate(templateId);

      if (!template) {
        throw new Error('Template not found');
      }

      // Generate unique session code using LiveSessionService
      let sessionCode = generateSessionCode();
      let codeExists = true;
      
      // Ensure code is unique
      while (codeExists) {
        const existing = await LiveSessionService.getByCode(sessionCode);
        if (!existing) {
          codeExists = false;
        } else {
          sessionCode = generateSessionCode();
        }
      }

      // Create instance in Firestore
      // Normalize questions to ensure correctAnswerIndices is always an array of numbers
      const normalizedQuestions = (template.questions || []).map(q => normalizeQuestion(q));
      log.debug('Starting instance with normalized questions:', normalizedQuestions);
      
      const instanceData = {
        templateId,
        courseId: template.courseId || null,
        name: customName || `${template.name} - ${new Date().toLocaleDateString()}`,
        sessionCode,
        owner: user.uid,
        status: 'active',
        questions: normalizedQuestions,
        settings: template.settings || {},
        startedAt: Date.now(),
        endedAt: null,
        currentQuestion: -1,         // -1 = Lobby (waiting for teacher to start)
        currentQuestionIndex: -1,    // -1 = Lobby (waiting for teacher to start)
        showResults: false,
        showCorrectAnswer: false,
        answersLocked: false,
        timerState: null,
        participants: {},
        answers: {},
        answer_validation: {} // Private answer validation (owner-only readable)
      };

      const instanceId = await createInstanceFirestore(instanceData);

      // Initialize live session state in RTDB using LiveSessionService
      await LiveSessionService.initializeLiveState(user.uid, instanceId, instanceData);

      // Create session code mapping using LiveSessionService
      await LiveSessionService.createSessionCode(sessionCode, user.uid, instanceId);

      // Increment template use count
      await incrementUseCount(templateId);

      // Add to local state
      const newInstance = { id: instanceId, ...instanceData };
      setInstances(prev => [newInstance, ...prev]);

      return newInstance;
    } catch (err) {
      log.error('Error starting instance:', err);
      throw new Error('Error starting quiz instance');
    }
  };

  // End an instance in both Firestore and Realtime DB
  const endInstance = async (instanceId) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const endData = {
        status: 'completed',
        endedAt: Date.now()
      };

      // Update Firestore
      await updateInstanceFirestore(instanceId, endData);

      // Update Realtime DB for live session
      const realtimeInstanceRef = ref(database, `quiz_instances/${user.uid}/${instanceId}`);
      await update(realtimeInstanceRef, endData);

      // Update local state
      setInstances(prev =>
        prev.map(instance =>
          instance.id === instanceId
            ? { ...instance, ...endData }
            : instance
        )
      );
    } catch (err) {
      log.error('Error ending instance:', err);
      throw new Error('Error ending quiz instance');
    }
  };

  // Get a single instance by ID from Firestore
  const getInstance = async (instanceId) => {
    if (!user) throw new Error('Not authenticated');

    try {
      // Check local state first
      const localInstance = instances.find(i => i.id === instanceId);
      if (localInstance) return localInstance;

      // If not in local state, fetch from Firestore
      const instance = await getInstanceFirestore(instanceId);
      return instance;
    } catch (err) {
      log.error('Error getting instance:', err);
      throw new Error('Error getting instance');
    }
  };

  // Get instance by session code (for participants) - uses LiveSessionService
  const getInstanceByCode = async (sessionCode) => {
    try {
      const sessionInfo = await LiveSessionService.getByCode(sessionCode);

      if (sessionInfo) {
        const { userId, instanceId } = sessionInfo;
        
        // Fetch instance metadata from Firestore
        const instance = await getInstanceFirestore(instanceId);
        
        if (instance) {
          return { id: instanceId, userId, ...instance };
        }
      }
      return null;
    } catch (err) {
      log.error('Error getting instance by code:', err);
      throw new Error('Error finding quiz');
    }
  };

  // Archive an instance in Firestore
  const archiveInstance = async (instanceId) => {
    if (!user) throw new Error('Not authenticated');

    try {
      await updateInstanceFirestore(instanceId, {
        status: 'archived'
      });

      // Update local state
      setInstances(prev =>
        prev.map(instance =>
          instance.id === instanceId
            ? { ...instance, status: 'archived' }
            : instance
        )
      );
    } catch (err) {
      log.error('Error archiving instance:', err);
      throw new Error('Error archiving instance');
    }
  };

  // Delete an instance completely
  const deleteInstance = async (instanceId) => {
    if (!user) throw new Error('Not authenticated');

    try {
      // Delete from Firestore
      await deleteInstanceFirestore(instanceId);

      // Delete from Realtime DB
      const realtimeInstanceRef = ref(database, `quiz_instances/${user.uid}/${instanceId}`);
      await set(realtimeInstanceRef, null);

      // Remove activities for this session from all students
      try {
        await removeActivitiesForSession(instanceId);
      } catch (error) {
        log.error('Error removing activities for session, but continuing:', error);
        // Don't throw - deletion should succeed even if activity cleanup fails
      }

      // Update local state
      setInstances(prev => prev.filter(instance => instance.id !== instanceId));
    } catch (err) {
      log.error('Error deleting instance:', err);
      throw new Error('Error deleting quiz instance');
    }
  };

  const value = {
    instances,
    loading,
    error,
    loadInstances,
    getInstancesByTemplate,
    getInstancesByCourse,
    getActiveInstances,
    startInstance,
    endInstance,
    deleteInstance,
    getInstance,
    getInstanceByCode,
    archiveInstance
  };

  return (
    <InstanceContext.Provider value={value}>
      {children}
    </InstanceContext.Provider>
  );
}

// Custom hook to use instance context
export function useInstances() {
  const context = useContext(InstanceContext);
  if (!context) {
    throw new Error('useInstances must be used within an InstanceProvider');
  }
  return context;
}

export default InstanceContext;
